import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";

import {
  CAMPAIGN_STATUS_COLORS,
  CAMPAIGN_STATUS_LABELS,
  CHANNEL_LABELS
} from "../../constants";
import { describeSegment } from "../../helpers";
import { MarketingCampaign } from "../../marketing.types";
import { StatusDot } from "../StatusDot";

import styles from "./CampaignCards.module.scss";

interface CampaignCardsProps {
  campaigns: MarketingCampaign[];
  isLoading?: boolean;
}

const CampaignCards = ({ campaigns, isLoading }: CampaignCardsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (isLoading) {
    return <Skeleton count={3} height={140} borderRadius={16} />;
  }

  if (!campaigns.length) {
    return <p className={styles.empty}>{t("No campaigns yet")}</p>;
  }

  return (
    <div className={styles.grid}>
      {campaigns.map((campaign) => (
        <button
          key={campaign.id}
          type="button"
          className={styles.card}
          onClick={() => navigate(`/marketing/campaigns/${campaign.id}`)}
        >
          <div className={styles.card__header}>
            <span className={styles.card__name}>{campaign.name}</span>
            <StatusDot
              color={CAMPAIGN_STATUS_COLORS[campaign.status]}
              label={CAMPAIGN_STATUS_LABELS[campaign.status]}
            />
          </div>

          <span className={styles.card__audience}>
            {describeSegment(campaign.segmentDefinition)}
            {" · "}
            {campaign.channel
              ? CHANNEL_LABELS[campaign.channel]
              : t("Auto channel")}
          </span>

          {campaign.launchedAt ? (
            <div className={styles.card__stats}>
              <span className={styles.card__stat}>
                <span className={styles.card__statValue}>
                  {campaign.sentCount}
                </span>
                {t("sent")}
              </span>
              <span className={styles.card__stat}>
                <span className={styles.card__statValueMuted}>
                  {campaign.skippedCount}
                </span>
                {t("skipped")}
              </span>
              <span className={styles.card__date}>
                {moment(campaign.launchedAt).format("DD/MM/YYYY HH:mm")}
              </span>
            </div>
          ) : (
            <span className={styles.card__draftNote}>
              {t("Draft — not launched yet")}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

export default CampaignCards;
